import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/stores/authStore';
import { TeamSettingsRefresher } from '@/components/TeamSettingsRefresher';
import { TeamThemeProvider } from '@/components/TeamThemeProvider';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { HintLayer } from '@/components/HintLayer';
import { ConfirmDialogHost } from '@/components/ConfirmDialog';
import { AuthenticatedLayout } from '@/components/layout/AuthenticatedLayout';
import { TeamLayout } from '@/components/layout/TeamLayout';
import { SessionUnreachableScreen } from '@/components/layout/SessionUnreachable';
import { LoadingState } from '@/components/LoadingState';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { AppUpdateBanner } from '@/components/AppUpdateBanner';
import { OfflineNotice } from '@/components/OfflineNotice';
import { RequirePermission } from '@/components/RequirePermission';
import { NotFoundPage } from '@/components/NotFoundPage';
import { AUTH_IN_PROGRESS, clearAuthMarkers } from '@/lib/auth-prefs';
import { readPref } from '@/lib/ui-prefs';
import { buildLoginPath, sanitizeReturnPath } from '@/lib/auth-redirect';
import { ReportFeatureGate } from '@/reports/ReportFeatureGate';
import { lazyPage } from '@/lib/lazy-page';

// Import pages
// Pages load on demand, so the first visit downloads the shell and the page it lands on,
// not all ~70 pages. Layouts show a loading state while a page's code arrives.
const HomePage = lazyPage(() => import('@/pages/home'));
const LoginPage = lazyPage(() => import('@/pages/login'));
const SelectTeamPage = lazyPage(() => import('@/pages/select-team'));
const NoTeamPage = lazyPage(() => import('@/pages/no-team'));
const ProductsPage = lazyPage(() => import('@/pages/products'));
const ProductsBulkEditPage = lazyPage(() => import('@/pages/products-bulk-edit'));
const ProductBatchesPage = lazyPage(() => import('@/pages/product-batches'));
const ProductBatchDetailsPage = lazyPage(() => import('@/pages/product-batch-details'));
const TasksPage = lazyPage(() => import('@/pages/tasks'));
const InboxPage = lazyPage(() => import('@/pages/inbox'));
const InboxSettingsPage = lazyPage(() => import('@/pages/inbox-settings'));
const PoliciesPage = lazyPage(() => import('@/pages/policies'));
const ExtensionConnectPage = lazyPage(() => import('@/pages/extension-connect'));
const MediaPage = lazyPage(() => import('@/pages/media'));
const CollectionsPage = lazyPage(() => import('@/pages/collections'));
const CollectionsBulkEditPage = lazyPage(() => import('@/pages/collections-bulk-edit'));
const CollectionsGapsPage = lazyPage(() => import('@/pages/collections-gaps'));
const IntegrationsPage = lazyPage(() => import('@/pages/integrations'));
const TeamPage = lazyPage(() => import('@/pages/team'));
const TeamSettingsPage = lazyPage(() => import('@/pages/team-settings'));
const LinkShopifyPage = lazyPage(() => import('@/pages/link-shopify'));
const AiUsagePage = lazyPage(() => import('@/pages/ai-usage'));
const AiPromptsIndexPage = lazyPage(() => import('@/pages/ai-prompts'));
const AiPromptOperationPage = lazyPage(() => import('@/pages/ai-prompts/operation-detail'));
const XeroCallbackPage = lazyPage(() => import('@/pages/xero-callback'));
const GoogleCallbackPage = lazyPage(() => import('@/pages/google-callback'));
const GmailCallbackPage = lazyPage(() => import('@/pages/gmail-callback'));
const BigQueryCallbackPage = lazyPage(() => import('@/pages/bigquery-callback'));
const MicrosoftCallbackPage = lazyPage(() => import('@/pages/microsoft-callback'));
const MetaCallbackPage = lazyPage(() => import('@/pages/meta-callback'));
const ShopifyCallbackPage = lazyPage(() => import('@/pages/shopify-callback'));
// The UI gallery is local dev only: production builds drop the route and the chunk.
const DevUiPage = import.meta.env.DEV ? lazyPage(() => import('@/pages/dev-ui')) : null;
// The capacity planner (JF-T-21) is a first look at the numbers, local dev only for now.
const VariantDetailsPage = lazyPage(() => import('@/pages/variant-details'));
const ProductDetailsPage = lazyPage(() => import('@/pages/product-details'));
const PrivacyPolicyPage = lazyPage(() => import('@/pages/legal/privacy'));
const DataDeletionPage = lazyPage(() => import('@/pages/legal/data-deletion'));
const TermsOfServicePage = lazyPage(() => import('@/pages/legal/terms'));
const ProductContentQueuePage = lazyPage(() => import('@/pages/product-content-queue'));
const VendorsPage = lazyPage(() => import('@/pages/vendors'));
const ProductTypesPage = lazyPage(() => import('@/pages/product-types'));
const ProductTypeNounsPage = lazyPage(() => import('@/pages/product-types/nouns'));
const ProductTypeNounBuilderPage = lazyPage(() => import('@/pages/product-types/noun-builder'));
const SwatchesPage = lazyPage(() => import('@/pages/swatches'));
const VendorDetailsPage = lazyPage(() => import('@/pages/vendor-details'));
const AcceptInvitePage = lazyPage(() => import('@/pages/accept-invite'));
const SuppliersPage = lazyPage(() => import('@/pages/suppliers'));
const SupplierCataloguesPage = lazyPage(() => import('@/pages/suppliers/catalogues'));
const SupplierDetailsPage = lazyPage(() => import('@/pages/supplier-details'));
const PlanningPage = lazyPage(() => import('@/pages/planning'));
const ImportsOverviewPage = lazyPage(() => import('@/pages/imports'));
const ImportsSupplierPage = lazyPage(() => import('@/pages/imports/supplier'));
const ImportsPriceListsPage = lazyPage(() => import('@/pages/imports/price-lists'));
const ImportsPriceListPage = lazyPage(() => import('@/pages/imports/price-list'));
const ImportsShipmentsPage = lazyPage(() => import('@/pages/imports/shipments'));
const ImportsShipmentPage = lazyPage(() => import('@/pages/imports/shipment'));
const ImportsNewShipmentPage = lazyPage(() => import('@/pages/imports/new-shipment-page'));
const ImportsPriceChangesPage = lazyPage(() => import('@/pages/imports/price-changes'));
const ImportsSettingsPage = lazyPage(() => import('@/pages/imports/settings'));
const PurchaseOrdersPage = lazyPage(() => import('@/pages/planning/purchase-orders'));
const ReceivingPage = lazyPage(() => import('@/pages/receiving'));
const ReceivingSessionPage = lazyPage(() => import('@/pages/receiving/session'));
const ReceivingReviewPage = lazyPage(() => import('@/pages/receiving/review'));
const WholesalePage = lazyPage(() => import('@/pages/wholesale'));
const WholesaleSessionPage = lazyPage(() => import('@/pages/wholesale/session'));
const WholesalePricingPage = lazyPage(() => import('@/pages/wholesale/pricing'));
const WholesalePriceListsPage = lazyPage(() => import('@/pages/wholesale/price-lists'));
const WholesaleOrdersPage = lazyPage(() => import('@/pages/wholesale/orders'));
const WholesaleOrderPage = lazyPage(() => import('@/pages/wholesale/order'));
const NewWholesaleOrderPage = lazyPage(() => import('@/pages/wholesale/new-order'));
const WholesaleCustomersPage = lazyPage(() => import('@/pages/wholesale/customers'));
const WholesaleCustomerPage = lazyPage(() => import('@/pages/wholesale/customer'));
const WholesalePortalPage = lazyPage(() => import('@/pages/wholesale/portal'));
const TradeDiscountsPage = lazyPage(() => import('@/pages/trade/discounts'));
const TradePortalPage = lazyPage(() => import('@/pages/trade/portal'));
const LabelsPage = lazyPage(() => import('@/pages/labels'));
const ReportsPage = lazyPage(() => import('@/reports'));
const SalesDashboardPage = lazyPage(() => import('@/reports/sales-dashboard'));
const ForecastReportPage = lazyPage(() => import('@/reports/forecast'));
const OutOfStockReportPage = lazyPage(() => import('@/reports/out-of-stock'));
const PriceChangesReportPage = lazyPage(() => import('@/reports/price-changes'));
const SupplierChangesReportPage = lazyPage(() => import('@/reports/supplier-changes'));
const UnitSalesReportPage = lazyPage(() => import('@/reports/unit-sales'));
const SkuStockPerformanceReportPage = lazyPage(() => import('@/reports/sku-stock-performance'));
const XeroStockNotOnShopifyReportPage = lazyPage(() => import('@/reports/xero-stock-not-on-shopify'));
const CollectionProblemsReportPage = lazyPage(() => import('@/reports/collection-problems'));
const UnseenStockReportPage = lazyPage(() => import('@/reports/unseen-stock'));
const SwatchProblemsReportPage = lazyPage(() => import('@/reports/swatch-problems'));
const MetaAdsCheckReportPage = lazyPage(() => import('@/reports/meta-ads-check'));
const WastedAdSpendReportPage = lazyPage(() => import('@/reports/wasted-ad-spend'));
const GoogleAdsCheckReportPage = lazyPage(() => import('@/reports/google-ads-check'));
const OrphanVariantRevenueReportPage = lazyPage(() => import('@/reports/orphan-variant-revenue'));
const OverstockReportPage = lazyPage(() => import('@/reports/overstock'));
const MarkdownsReportPage = lazyPage(() => import('@/reports/markdowns'));
const ShopifySalesReportPage = lazyPage(() => import('@/reports/shopify-sales'));
const ShopifyPaymentsReportPage = lazyPage(() => import('@/reports/shopify-payments'));
const InboxWorkloadReportPage = lazyPage(() => import('@/reports/inbox-workload'));
const CapacityPlannerReportPage = lazyPage(() => import('@/reports/capacity-planner'));
const TimeSpentReportPage = lazyPage(() => import('@/reports/time-spent'));
const OrderReconciliationReportPage = lazyPage(() => import('@/reports/order-reconciliation'));
const MarketingOverviewPage = lazyPage(() => import('@/pages/marketing'));
const GoogleAdsDetailPage = lazyPage(() => import('@/pages/marketing/google-ads'));
const GoogleAdsBrandCoveragePage = lazyPage(() => import('@/pages/marketing/google-ads/brands'));
const GoogleAdsFeedHealthPage = lazyPage(() => import('@/pages/marketing/google-ads/feed'));
const GoogleFeedControlPage = lazyPage(() => import('@/pages/marketing/google-ads/feed-control'));
const GoogleAdsTermCategoriesPage = lazyPage(() => import('@/pages/marketing/google-ads/term-categories'));
const GoogleAdsSearchTermsPage = lazyPage(() => import('@/pages/marketing/google-ads/search-terms'));
const GoogleAdsPmaxPage = lazyPage(() => import('@/pages/marketing/google-ads/pmax'));
const PmaxAssetGroupPage = lazyPage(() => import('@/pages/marketing/google-ads/pmax-asset-group'));
const GoogleAdsBudgetsPage = lazyPage(() => import('@/pages/marketing/google-ads/budgets'));
const MicrosoftAdsDetailPage = lazyPage(() => import('@/pages/marketing/microsoft-ads'));
const MetaAdsDetailPage = lazyPage(() => import('@/pages/marketing/meta-ads'));
const MetaAdSetsPage = lazyPage(() => import('@/pages/marketing/meta-ads/adsets'));
const MetaPostAdsPage = lazyPage(() => import('@/pages/marketing/meta-ads/post-ads'));
const InstagramMarketingPage = lazyPage(() => import('@/pages/marketing/instagram'));
const SocialPicksPage = lazyPage(() => import('@/pages/marketing/social-picks'));
const PostIdeasPage = lazyPage(() => import('@/pages/marketing/post-ideas'));
const CustomerNotificationsPage = lazyPage(() => import('@/pages/marketing/notifications'));
const NewslettersPage = lazyPage(() => import('@/pages/marketing/newsletters'));
const NewsletterComposerPage = lazyPage(() => import('@/pages/marketing/newsletters/composer'));
const NewsletterSettingsPage = lazyPage(() => import('@/pages/marketing/newsletters/settings'));
const LandingPageEditorPage = lazyPage(() => import('@/pages/marketing/landing-pages/editor'));
const LandingPagesPage = lazyPage(() => import('@/pages/marketing/landing-pages/list'));
const FacebookMarketingPage = lazyPage(() => import('@/pages/marketing/facebook'));
const MarketingSeoPage = lazyPage(() => import('@/pages/marketing/seo'));
const SeoNearMissesPage = lazyPage(() => import('@/pages/marketing/seo-near-misses'));
const MarketingSettingsPage = lazyPage(() => import('@/pages/marketing/settings'));

// Protected route component that requires active team
const TeamRequiredRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, sessionUnreachable } = useAuth();
  const authUser = useAuthStore(state => state.user);
  const teamExit = useAuthStore(state => state.teamExit);
  const location = useLocation();

  // Leaving this team: the team pages unmount before the next session is stored (utils/team-exit.ts).
  if (teamExit) {
    return <LoadingState fullPage label={teamExit.label} />;
  }

  if (loading) {
    return <LoadingState fullPage label="Loading…" />;
  }

  // jflo didn't answer the session check: not signed out, so not /login (decision 11 a).
  if (sessionUnreachable) {
    return <SessionUnreachableScreen />;
  }

  if (!user) {
    return (
      <Navigate
        to={buildLoginPath(`${location.pathname}${location.search}${location.hash}`)}
        replace
      />
    );
  }

  if (!user.teams?.length) {
    const pendingInvite = authUser?.pendingInviteToken;
    if (pendingInvite) {
      return <Navigate to={`/accept-invite/${pendingInvite}?from=login`} replace />;
    }
    // jflo is invite only (T-74): no team means asking a team's admin for an invite.
    return <Navigate to="/no-team" replace />;
  }

  const hasActiveTeam = authUser?.active_team?.id;

  if (!hasActiveTeam) {
    // Come back here once a team is chosen (the Chrome extension's sign-in window relies on it).
    const back = sanitizeReturnPath(`${location.pathname}${location.search}`);
    return <Navigate to={back === '/' ? '/select-team' : `/select-team?redirect=${encodeURIComponent(back)}`} replace />;
  }

  const isTeamActive = authUser?.active_team?.status === 'active';

  if (!isTeamActive) {
    const path = location.pathname;
    const allowedPaths = ['/', '/team'];

    if (!allowedPaths.includes(path)) {
      return <Navigate to="/" replace />;
    }
  }

  return <TeamLayout>{children}</TeamLayout>;
};

// Protected route component that requires authentication
const AuthRequiredRoute = ({ children }: { children: React.ReactNode }) => {
  const { loading, sessionUnreachable } = useAuth();
  const user = useAuthStore(state => state.user);
  const location = useLocation();
  const [showLoading, setShowLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading && !user) {
        if (readPref(AUTH_IN_PROGRESS)) {
          console.log('Auth appears stuck, forcing page reload');
          clearAuthMarkers();
          window.location.reload();
        }
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [loading, user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // A brand-new tab starts with no user (sessions are per tab), so wait for the session exchange
  // however long it takes rather than sending it to /login.
  if (loading && (showLoading || !user)) {
    return <LoadingState fullPage label="Loading…" />;
  }

  // With a stored user the page carries on (an OAuth callback mid-way); without one, offer Retry.
  if (sessionUnreachable && !user) {
    return <SessionUnreachableScreen />;
  }

  if (!user) {
    return (
      <Navigate
        to={buildLoginPath(`${location.pathname}${location.search}${location.hash}`)}
        replace
      />
    );
  }

  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
};

/** Link Shopify reconnects a team's own store; with no team there's nothing to reconnect (T-74). */
const TeamMemberRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useAuthStore(state => state.user);
  if (user && !user.teams?.length) return <Navigate to="/no-team" replace />;
  return <>{children}</>;
};

// Main app component
export default function App() {
  return (
    <TeamThemeProvider>
      <TooltipProvider>
        <AuthProvider>
          <TeamSettingsRefresher />
          <Router>
            {/* One hover-text layer for the whole app, above every layout: mark elements with data-hint. */}
            <HintLayer />
            <ConfirmDialogHost />
            <React.Suspense fallback={<LoadingState fullPage label="Loading…" />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<PublicLayout><LoginPage /></PublicLayout>} />
              {/* Policies Meta and Google link to; readable without logging in. */}
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/data-deletion" element={<DataDeletionPage />} />
              <Route path="/select-team" element={<PublicLayout><SelectTeamPage /></PublicLayout>} />
              <Route path="/no-team" element={<PublicLayout><NoTeamPage /></PublicLayout>} />

              <Route
                path="/accept-invite/:token"
                element={
                  <React.Suspense fallback={<LoadingState fullPage label="Loading invite page…" />}>
                    <PublicLayout>
                      <AcceptInvitePage />
                    </PublicLayout>
                  </React.Suspense>
                }
              />

              {/* Auth required routes */}
              <Route
                path="/link-shopify"
                element={
                  <AuthRequiredRoute>
                    <TeamMemberRoute>
                      <LinkShopifyPage />
                    </TeamMemberRoute>
                  </AuthRequiredRoute>
                }
              />

              <Route
                path="/xero-callback"
                element={
                  <AuthRequiredRoute>
                    <XeroCallbackPage />
                  </AuthRequiredRoute>
                }
              />

              <Route
                path="/google-callback"
                element={
                  <AuthRequiredRoute>
                    <GoogleCallbackPage />
                  </AuthRequiredRoute>
                }
              />

              <Route
                path="/gmail-callback"
                element={
                  <AuthRequiredRoute>
                    <GmailCallbackPage />
                  </AuthRequiredRoute>
                }
              />

              <Route
                path="/bigquery-callback"
                element={
                  <AuthRequiredRoute>
                    <BigQueryCallbackPage />
                  </AuthRequiredRoute>
                }
              />

              <Route
                path="/shopify-callback"
                element={
                  <AuthRequiredRoute>
                    <ShopifyCallbackPage />
                  </AuthRequiredRoute>
                }
              />

              <Route
                path="/microsoft-callback"
                element={
                  <AuthRequiredRoute>
                    <MicrosoftCallbackPage />
                  </AuthRequiredRoute>
                }
              />

              <Route
                path="/meta-callback"
                element={
                  <AuthRequiredRoute>
                    <MetaCallbackPage />
                  </AuthRequiredRoute>
                }
              />

              {/* Team required routes */}
              <Route
                path="/"
                element={
                  <TeamRequiredRoute>
                    <HomePage />
                  </TeamRequiredRoute>
                }
              />

              {/* One route, so opening a task over the Board keeps the Board (and its scroll) mounted. */}
              <Route
                path="/tasks/:taskId?"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="TASKS" what="Tasks">
                      <TasksPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />
              {/* The Chrome extension's sign-in window. */}
              <Route
                path="/extension/connect"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="TASKS" what="Tasks">
                      <ExtensionConnectPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />
              {/* One route, so opening a policy keeps the page (and its list) mounted. */}
              <Route
                path="/policies/:policyId?"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="POLICIES" what="Policies">
                      <PoliciesPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />
              {/* Saved replies, signatures, who handles what, topic labels, sender rules: /inbox-settings/<tab>/<id>. */}
              <Route
                path="/inbox-settings/:tab?/:itemId?"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="INBOX" what="Inbox">
                      <InboxSettingsPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />
              {/* One route, so opening a Conversation keeps the page (and its list) mounted. */}
              <Route
                path="/inbox/:conversationId?"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="INBOX" what="Inbox">
                      <InboxPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/products"
                element={
                  <TeamRequiredRoute>
                    <ProductsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/product-batches"
                element={
                  <TeamRequiredRoute>
                    <ProductBatchesPage />
                  </TeamRequiredRoute>
                }
              />
              <Route
                path="/product-batches/:batchId"
                element={
                  <TeamRequiredRoute>
                    <ProductBatchDetailsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/products-bulk-edit"
                element={
                  <TeamRequiredRoute>
                    <ProductsBulkEditPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/media"
                element={
                  <TeamRequiredRoute>
                    <MediaPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/labels"
                element={
                  <TeamRequiredRoute>
                    <LabelsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/collections"
                element={
                  <TeamRequiredRoute>
                    <CollectionsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/collections-bulk-edit"
                element={
                  <TeamRequiredRoute>
                    <CollectionsBulkEditPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/collections-gaps"
                element={
                  <TeamRequiredRoute>
                    <CollectionsGapsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/integrations"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="SYNC_SETUP" what="Integrations">
                      <IntegrationsPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/sync"
                element={
                  <TeamRequiredRoute>
                    <Navigate to="/integrations?tab=sync" replace />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/xero-posting"
                element={
                  <TeamRequiredRoute>
                    <Navigate to="/integrations?tab=xero&view=posting" replace />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/team"
                element={
                  <TeamRequiredRoute>
                    <TeamPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/team-settings"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="TEAM_MEMBER_MANAGE" what="Team settings">
                      <TeamSettingsPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/ai-usage"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="AI_PROMPTS_MANAGE" what="AI usage">
                      <AiUsagePage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/ai-prompts"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="AI_PROMPTS_MANAGE" what="AI prompts">
                      <AiPromptsIndexPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/ai-prompts/:operation"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="AI_PROMPTS_MANAGE" what="AI prompts">
                      <AiPromptOperationPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/variant-details/:id"
                element={
                  <TeamRequiredRoute>
                    <VariantDetailsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/product-details/:id"
                element={
                  <TeamRequiredRoute>
                    <ProductDetailsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/product-content-queue"
                element={
                  <TeamRequiredRoute>
                    <ProductContentQueuePage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/vendors"
                element={
                  <TeamRequiredRoute>
                    <VendorsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/vendors/:id"
                element={
                  <TeamRequiredRoute>
                    <VendorDetailsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/product-types"
                element={
                  <TeamRequiredRoute>
                    <ProductTypesPage />
                  </TeamRequiredRoute>
                }
              />
              <Route
                path="/product-types/nouns"
                element={
                  <TeamRequiredRoute>
                    <ProductTypeNounsPage />
                  </TeamRequiredRoute>
                }
              />
              <Route
                path="/product-types/nouns/:id"
                element={
                  <TeamRequiredRoute>
                    <ProductTypeNounBuilderPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/swatches"
                element={
                  <TeamRequiredRoute>
                    <SwatchesPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/suppliers"
                element={
                  <TeamRequiredRoute>
                    <SuppliersPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/suppliers/catalogues"
                element={
                  <TeamRequiredRoute>
                    <SupplierCataloguesPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/suppliers/:id"
                element={
                  <TeamRequiredRoute>
                    <SupplierDetailsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/planning"
                element={
                  <TeamRequiredRoute>
                    <PlanningPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/planning/purchase-orders"
                element={
                  <TeamRequiredRoute>
                    <PurchaseOrdersPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/imports"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="IMPORTS_VIEW" what="Imports">
                      <ImportsOverviewPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/imports/suppliers/:supplierId"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="IMPORTS_VIEW" what="Imports">
                      <ImportsSupplierPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/imports/price-lists"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="IMPORTS_VIEW" what="Imports">
                      <ImportsPriceListsPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/imports/price-lists/:id"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="IMPORTS_VIEW" what="Imports">
                      <ImportsPriceListPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/imports/shipments"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="IMPORTS_VIEW" what="Imports">
                      <ImportsShipmentsPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/imports/shipments/new"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="IMPORTS_VIEW" what="Imports">
                      <ImportsNewShipmentPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/imports/shipments/:id"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="IMPORTS_VIEW" what="Imports">
                      <ImportsShipmentPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/imports/price-changes"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="IMPORTS_VIEW" what="Imports">
                      <ImportsPriceChangesPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/imports/settings"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="IMPORTS_VIEW" what="Imports">
                      <ImportsSettingsPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/receiving"
                element={
                  <TeamRequiredRoute>
                    <ReceivingPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/receiving/:sessionId"
                element={
                  <TeamRequiredRoute>
                    <ReceivingSessionPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/receiving/:sessionId/review"
                element={
                  <TeamRequiredRoute>
                    <ReceivingReviewPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/wholesale"
                element={
                  <TeamRequiredRoute>
                    <WholesalePage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/wholesale/pricing"
                element={
                  <TeamRequiredRoute>
                    <WholesalePricingPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/wholesale/price-lists"
                element={
                  <TeamRequiredRoute>
                    <WholesalePriceListsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/wholesale/portal"
                element={
                  <TeamRequiredRoute>
                    <WholesalePortalPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/wholesale/orders"
                element={
                  <TeamRequiredRoute>
                    <WholesaleOrdersPage />
                  </TeamRequiredRoute>
                }
              />

              {/* T-15: an order staff make for a customer. */}
              <Route
                path="/wholesale/orders/new"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="WHOLESALE_EDIT" what="New order">
                      <NewWholesaleOrderPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/wholesale/orders/:id"
                element={
                  <TeamRequiredRoute>
                    <WholesaleOrderPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/wholesale/customers"
                element={
                  <TeamRequiredRoute>
                    <WholesaleCustomersPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/wholesale/customers/:id"
                element={
                  <TeamRequiredRoute>
                    <WholesaleCustomerPage />
                  </TeamRequiredRoute>
                }
              />

              {/* Trade (T-218): the portal's trade customers, their orders and discounts. */}
              <Route
                path="/trade/orders"
                element={
                  <TeamRequiredRoute>
                    <WholesaleOrdersPage key="trade" kind="trade" />
                  </TeamRequiredRoute>
                }
              />

              {/* T-15: an order staff make for a customer. */}
              <Route
                path="/trade/orders/new"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="WHOLESALE_EDIT" what="New order">
                      <NewWholesaleOrderPage key="trade-new" kind="trade" />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/trade/orders/:id"
                element={
                  <TeamRequiredRoute>
                    <WholesaleOrderPage key="trade" />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/trade/customers"
                element={
                  <TeamRequiredRoute>
                    <WholesaleCustomersPage key="trade" kind="trade" />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/trade/customers/:id"
                element={
                  <TeamRequiredRoute>
                    <WholesaleCustomerPage key="trade" />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/trade/discounts"
                element={
                  <TeamRequiredRoute>
                    <TradeDiscountsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/trade/portal"
                element={
                  <TeamRequiredRoute>
                    <TradePortalPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/wholesale/:sessionId"
                element={
                  <TeamRequiredRoute>
                    <WholesaleSessionPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports"
                element={
                  <TeamRequiredRoute>
                    <ReportsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/sales-dashboard"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="sales-dashboard">
                      <SalesDashboardPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/forecast"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="forecast">
                      <ForecastReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/out-of-stock"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="out-of-stock">
                      <OutOfStockReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/price-changes"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="price-changes">
                      <PriceChangesReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/supplier-changes"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="supplier-changes">
                      <SupplierChangesReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/unit-sales"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="unit-sales">
                      <UnitSalesReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/sku-stock-performance"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="sku-stock-performance">
                      <SkuStockPerformanceReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/shopify-sales"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="shopify-sales">
                      <ShopifySalesReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/capacity-planner"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="capacity-planner">
                      <CapacityPlannerReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/inbox-workload"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="inbox-workload">
                      <InboxWorkloadReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/shopify-payments"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="shopify-payments">
                      <ShopifyPaymentsReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/time-spent"
                element={
                  <TeamRequiredRoute>
                    <TimeSpentReportPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/order-reconciliation"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="order-reconciliation">
                      <OrderReconciliationReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/xero-stock-not-on-shopify"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="xero-stock-not-on-shopify">
                      <XeroStockNotOnShopifyReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/unseen-stock"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="unseen-stock">
                      <UnseenStockReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/collection-problems"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="collection-problems">
                      <CollectionProblemsReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/meta-ads-check"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="meta-ads-check">
                      <MetaAdsCheckReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/swatch-problems"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="swatch-problems">
                      <SwatchProblemsReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/google-ads-check"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="google-ads-check">
                      <GoogleAdsCheckReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/wasted-ad-spend"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="wasted-ad-spend">
                      <WastedAdSpendReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/orphan-variant-revenue"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="orphan-variant-revenue">
                      <OrphanVariantRevenueReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/overstock"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="overstock">
                      <OverstockReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/reports/markdowns"
                element={
                  <TeamRequiredRoute>
                    <ReportFeatureGate reportId="markdowns">
                      <MarkdownsReportPage />
                    </ReportFeatureGate>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing"
                element={
                  <TeamRequiredRoute>
                    <MarketingOverviewPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/google-ads"
                element={
                  <TeamRequiredRoute>
                    <GoogleAdsDetailPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/google-ads/brands"
                element={
                  <TeamRequiredRoute>
                    <GoogleAdsBrandCoveragePage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/google-ads/feed"
                element={
                  <TeamRequiredRoute>
                    <GoogleAdsFeedHealthPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/google-ads/feed-control"
                element={
                  <TeamRequiredRoute>
                    <GoogleFeedControlPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/google-ads/term-categories"
                element={
                  <TeamRequiredRoute>
                    <GoogleAdsTermCategoriesPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/google-ads/pmax"
                element={
                  <TeamRequiredRoute>
                    <GoogleAdsPmaxPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/google-ads/pmax/asset-groups/:id"
                element={
                  <TeamRequiredRoute>
                    <PmaxAssetGroupPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/google-ads/budgets"
                element={
                  <TeamRequiredRoute>
                    <GoogleAdsBudgetsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/google-ads/search-terms"
                element={
                  <TeamRequiredRoute>
                    <GoogleAdsSearchTermsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/microsoft-ads"
                element={
                  <TeamRequiredRoute>
                    <MicrosoftAdsDetailPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/meta-ads"
                element={
                  <TeamRequiredRoute>
                    <MetaAdsDetailPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/meta-ads/adsets"
                element={
                  <TeamRequiredRoute>
                    <MetaAdSetsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/meta-ads/post-ads"
                element={
                  <TeamRequiredRoute>
                    <MetaPostAdsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/instagram"
                element={
                  <TeamRequiredRoute>
                    <InstagramMarketingPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/social-picks"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="SOCIAL_PICKS" what="Social picks">
                      <SocialPicksPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/post-ideas"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="SOCIAL_PICKS" what="Social picks">
                      <PostIdeasPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/newsletters"
                element={
                  <TeamRequiredRoute>
                    <NewslettersPage />
                  </TeamRequiredRoute>
                }
              />
              <Route
                path="/marketing/newsletters/settings"
                element={
                  <TeamRequiredRoute>
                    <NewsletterSettingsPage />
                  </TeamRequiredRoute>
                }
              />
              <Route
                path="/marketing/newsletters/:id"
                element={
                  <TeamRequiredRoute>
                    <NewsletterComposerPage />
                  </TeamRequiredRoute>
                }
              />
              <Route
                path="/landing-pages"
                element={
                  <TeamRequiredRoute>
                    <LandingPagesPage />
                  </TeamRequiredRoute>
                }
              />
              <Route
                path="/landing-pages/:id"
                element={
                  <TeamRequiredRoute>
                    <LandingPageEditorPage />
                  </TeamRequiredRoute>
                }
              />
              {/* Where the editor was in T-386: links already shared still open it. */}
              <Route path="/marketing/landing-pages/:id" element={<LandingPageMoved />} />

              <Route
                path="/marketing/notifications"
                element={
                  <TeamRequiredRoute>
                    <CustomerNotificationsPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/facebook"
                element={
                  <TeamRequiredRoute>
                    <FacebookMarketingPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/seo"
                element={
                  <TeamRequiredRoute>
                    <MarketingSeoPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/seo/near-misses"
                element={
                  <TeamRequiredRoute>
                    <SeoNearMissesPage />
                  </TeamRequiredRoute>
                }
              />

              <Route
                path="/marketing/settings"
                element={
                  <TeamRequiredRoute>
                    <RequirePermission permission="SYNC_SETUP" what="Marketing settings">
                      <MarketingSettingsPage />
                    </RequirePermission>
                  </TeamRequiredRoute>
                }
              />

              {DevUiPage ? (
                <Route
                  path="/dev/ui"
                  element={
                    <TeamRequiredRoute>
                      <DevUiPage />
                    </TeamRequiredRoute>
                  }
                />
              ) : null}


              {/* Catch-all: "Page not found" inside the app (decision 61 a); signed-out visitors go to
                  /login first and come back here. */}
              <Route
                path="*"
                element={
                  <TeamRequiredRoute>
                    <NotFoundPage />
                  </TeamRequiredRoute>
                }
              />
            </Routes>
            </React.Suspense>
          </Router>
          <AppUpdateBanner />
          <OfflineNotice />
          <Toaster />
        </AuthProvider>
      </TooltipProvider>
    </TeamThemeProvider>
  );
}

/** The landing page editor moved from Marketing to Products › Landing pages (T-397). */
function LandingPageMoved() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/marketing\/landing-pages\//, '/landing-pages/')}${search}${hash}`} replace />;
}
